import { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdmins } from "../redux/features/admin/adminSlice";
import { ShimmerBadge, ShimmerTable } from "react-shimmer-effects";
import AddNewAdmin from "../components/admin/AddNewAdmin";
import UpdateAdmin from "../components/admin/UpdateAdmin";
import ViewAdmin from "../components/admin/ViewAdmin";
import DeleteAdmin from "../components/admin/DeleteAdmin";

function Admin() {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const { list, getAdminLoading, getAdminError } = useSelector(
    (state) => state.admin,
  );

  const refetchAdmins = async () => {
    if (initialFetchDone) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }

    await dispatch(fetchAdmins());
  };

  useEffect(() => {
    refetchAdmins()
      .then(() => {
        setInitialFetchDone(true);
      })
      .catch((err) => console.error("Initial admin fetch failed:", err));
  }, []);

  const filteredAdmins = list.filter((admin) => {
    const search = searchTerm.toLowerCase();
    return (
      admin.name?.toLowerCase().includes(search) ||
      admin.email?.toLowerCase().includes(search) ||
      admin.id?.toString().includes(search)
    );
  });

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };
  const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentAdmins = filteredAdmins.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  if (getAdminLoading) {
    return (
      <div className="min-h-screen bg-lightPurple/5 flex flex-col">
        <ToastContainer />
        <Navbar />
        <div className="flex-grow p-4 grid grid-cols-1 max-w-7xl w-full mx-auto">
          <div className="my-6 flex justify-between items-center">
            <h1 className="text-4xl font-bold leading-tight">Admin</h1>
            <div className="flex items-center">
              <AddNewAdmin onChange={refetchAdmins} />
            </div>
          </div>

          <div className="p-8 bg-gray-50 rounded-xl shadow-lg">
            <div className="flex justify-end mb-8">
              <ShimmerBadge width={250} />
            </div>
            <ShimmerTable row={5} col={4} gap={20} />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (getAdminError) {
    return (
      <div className="min-h-screen bg-lightPurple/5 flex flex-col">
        <ToastContainer />
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center text-center text-red-600">
          <p className="text-lg font-semibold">Error loading admins</p>
          <button
            onClick={() => dispatch(fetchAdmins())}
            className="mt-4 bg-lightPurple hover:bg-purpleColor text-white px-4 py-2 rounded-lg"
          >
            Retry
          </button>
        </div>
        <Footer />
      </div>
    );
  }
  return (
    <div className="min-h-screen flex flex-col bg-lightPurple/5">
      <ToastContainer />
      <Navbar />
      <div className="flex-grow p-4 grid grid-cols-1 max-w-7xl mx-auto w-full">
        <div className="flex justify-between items-center my-5">
          <h1 className="text-4xl font-bold">Admin</h1>
          <AddNewAdmin onChange={refetchAdmins} />
        </div>

        <div className="mb-[4.5rem] overflow-x-auto">
          {list.length === 0 ? (
            <p className="text-center text-gray-600 py-10">No admins found.</p>
          ) : (
            <div className="p-8 bg-gray-50 rounded-xl shadow-lg">
              <div className="flex justify-end mb-8">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-72 border border-gray-300 rounded-lg p-2 pl-4 shadow-sm focus:ring-2 focus:ring-purpleColor focus:outline-none transition"
                />
              </div>

              <div className="overflow-x-auto rounded-lg shadow-md">
                <table
                  className="min-w-full bg-white text-center border border-gray-200 rounded-xl"
                  style={{ scrollbarWidth: "thin" }}
                >
                  <thead className="bg-gradient-to-r from-lightPurple to-purpleColor text-white uppercase text-base font-semibold">
                    <tr>
                      <th className="p-4">ID</th>
                      <th className="p-4">Name</th>
                      <th className="p-4">Email</th>
                      <th className="p-4">Role</th>
                      <th className="p-4">Brand</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentAdmins.map((admin, index) => (
                      <tr
                        key={admin.id}
                        className={`transition hover:bg-purple-50 h-[5rem] ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                      >
                        <td className="p-4 text-gray-700 font-medium">
                          {admin.id}
                        </td>
                        <td className="p-4 text-gray-700">{admin.name}</td>
                        <td className="p-4 text-gray-600">{admin.email}</td>
                        <td className="p-4 text-gray-600">
                          {admin.roles?.[0]?.name || "-"}
                        </td>
                        <td className="p-4 text-gray-600">
                          {admin.brand?.name || "-"}
                        </td>
                        <td className="p-4">
                          <div className="flex justify-center gap-3">
                            <ViewAdmin adminId={admin.id} />
                            <UpdateAdmin
                              adminId={admin.id}
                              onChange={refetchAdmins}
                            />
                            <DeleteAdmin
                              adminId={admin.id}
                              onChange={refetchAdmins}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row justify-between items-center mt-12 gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-gray-700 font-medium">
                    Rows per page:
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="border border-gray-300 rounded-md px-2 py-1 bg-white shadow-sm focus:ring-2 focus:ring-purpleColor focus:outline-none transition"
                  >
                    <option value={5}>5</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium border transition ${
                      currentPage === 1
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:bg-purple-50 text-purpleColor border-purpleColor"
                    }`}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                    Prev
                  </button>

                  <span className="text-gray-700 font-medium">
                    Page {currentPage} of {totalPages || 1}
                  </span>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition ${
                      currentPage === totalPages
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-lightPurple text-white hover:bg-purpleColor"
                    }`}
                  >
                    Next
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <Footer />
      </div>
    </div>
  );
}

export default Admin;
